
import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Property } from '@/types';
import { useNewlyAddedProperties } from '@/hooks/useNewlyAddedProperties';
import { SpinnerIcon, ExclamationTriangleIcon, Squares2X2Icon, MapPinIcon } from '@/components/Icons';
import { NewDashboardHeader } from '@/components/NewDashboardHeader';
import { Footer } from '@/components/Footer';
import { PropertyCard } from '@/components/broker/PropertyCard';
import { PublicMapView } from '@/components/PublicMapView';
import { shortCodeMap } from '@/firebase/firestoreCollections';

interface NewlyAddedViewProps {
  dateStr: string;
  onLoginRedirect?: () => void;
}

export const NewlyAddedView: React.FC<NewlyAddedViewProps> = ({ dateStr, onLoginRedirect }) => {
  const { properties, loading, error } = useNewlyAddedProperties(dateStr);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');

  const handleViewProperty = (property: Property) => {
    const source = (property as any).sourceCollection || 'ciencuadras';
    const shortCode = Object.keys(shortCodeMap).find(key => shortCodeMap[key] === source) || 'c';
    window.open(`${window.location.origin}/share/${property.id}?collection=${shortCode}`, '_blank');
  };

  const renderContent = () => {
    if (loading) return <div className="flex flex-col items-center justify-center p-20 text-sky-600"><SpinnerIcon className="w-12 h-12 animate-spin mb-4" /><p>Cargando inmuebles recientes...</p></div>;
    if (error) return <div className="p-10 text-center bg-red-50 rounded-lg border border-red-200"><ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" /><p className="font-semibold text-red-800">{error}</p></div>;
    if (properties.length === 0) return <div className="p-20 text-center bg-gray-50 rounded-lg border border-dashed border-gray-300"><p className="text-gray-500 text-lg">No se encontraron inmuebles agregados recientemente en los últimos 8 días.</p></div>;

    return (
      <section className="bg-white p-4 sm:p-6 shadow-xl rounded-2xl border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center">
            <span className="bg-sky-100 text-sky-700 px-3 py-1 rounded-full text-sm font-bold mr-3">{properties.length}</span>
            Inmuebles Encontrados
          </h2>
          <div className="flex items-center bg-gray-100 p-1 rounded-xl self-start">
            <button onClick={() => setViewMode('grid')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'grid' ? 'bg-white text-sky-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><Squares2X2Icon className="w-4 h-4 mr-2"/>Cuadrícula</button>
            <button onClick={() => setViewMode('map')} className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${viewMode === 'map' ? 'bg-white text-sky-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><MapPinIcon className="w-4 h-4 mr-2"/>Mapa</button>
          </div>
        </div>
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {properties.map(p => (
              <PropertyCard
                key={p.id}
                property={p}
                isSelected={false}
                onView={() => handleViewProperty(p)}
                onShare={() => {}}
                onToggleSelect={() => {}}
                isCompact={false}
                source={(p as any).sourceCollection || 'ciencuadras'}
                appUser={null}
                onInterested={() => {}}
                hideSpecialStatus={true}
              />
            ))}
          </div>
        ) : <PublicMapView properties={properties} onViewProperty={handleViewProperty} />}
      </section>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <NewDashboardHeader />
      <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div className="border-l-4 border-sky-500 pl-6 py-2">
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Nuevos Inmuebles en Inventario</h1>
          <p className="text-gray-500 mt-2 text-lg">Explora las propiedades más recientes ingresadas al inventario en los últimos 8 días</p>
        </div>

        {onLoginRedirect && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl p-4 sm:p-6 lg:p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 overflow-hidden relative">
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full blur-2xl -mr-16 -mt-16 pointer-events-none"></div>
            <div className="space-y-1.5 z-10 flex flex-col items-center text-center md:items-start md:text-left">
              <span className="bg-white/20 text-white text-[9px] uppercase font-black tracking-wider px-2.5 py-0.5 rounded-full border border-white/20">Acceso Exclusivo de Brokers</span>
              <h2 className="text-lg sm:text-xl md:text-2xl font-extrabold tracking-tight leading-snug">¿Quieres filtrar inmuebles, agendar citas, revisar comisiones o ver todo nuestro portafolio de inventario detallado?</h2>
              <p className="text-xs sm:text-sm text-amber-50 max-w-2xl font-medium leading-relaxed">Inicia sesión con tu cuenta oficial para interactuar con todas las herramientas de venta, ver ubicaciones satelitales y acceder a contactos de propietarios de inmediato.</p>
            </div>
            
            <motion.button
              onClick={onLoginRedirect}
              className="z-10 bg-white text-orange-600 text-xs font-black uppercase tracking-wider py-3 px-6 md:py-3.5 md:px-8 rounded-full shadow-md border border-transparent whitespace-nowrap cursor-pointer flex items-center justify-center space-x-2 w-full md:w-auto"
              animate={{
                scale: [1, 1.03, 1],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
            >
              <span>🔑 Inicia sesión para verlos a detalle</span>
            </motion.button>
          </div>
        )}

        {renderContent()}
      </main>
      <Footer />
    </div>
  );
};
